import { CalendarIcon, ChevronDownIcon } from '@radix-ui/react-icons';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';
import { useEffect, useMemo, useState, type JSX } from 'react';
import { DateRange } from 'react-day-picker';

import { Button } from '../../../primitives/button';
import { Calendar } from '../../../primitives/calendar';
import { Input } from '../../../primitives/input';
import { LoadingAddon } from '../../../primitives/loading-addon';
import { Popover, PopoverContent, PopoverTrigger } from '../../../primitives/popover';
import { Separator } from '../../../primitives/separator';
import { LabelWithoutRef } from '../../label/label-without-ref';

import { capitalize } from '../../../utils/capitalize';
import { cn } from '../../../utils/cn';

import { buildDateWithTime } from './build-date-with-time';
import { getFormattedDate } from './get-formatted-date';
import { isValidTime } from './is-valid-time';
import { FORMAT_TIME, normalizeTimeFromDate } from './normalize-time-from-date';

import { MEXICO_TIMEZONE, SYSTEM_TIMEZONE } from './time-zone';

export { MEXICO_TIMEZONE };

type CalendarSingleProps = {
  mode?: 'single';
  date: Date | undefined;
  onChange: (date: Date | undefined) => void;
};

type CalendarRangeProps = {
  mode: 'range';
  date: DateRange | undefined;
  onChange: (range: DateRange | undefined) => void;
};

type CalendarDatePickerV2Props = (CalendarSingleProps | CalendarRangeProps) & {
  button?: {
    icon?: JSX.Element;
    id?: string;
    onClick: VoidFunction;
    text?: string;
  };
  defaultEndTime?: string;
  defaultStartTime?: string;
  disabled?: boolean;
  fromDate?: Date;
  hasTime?: boolean;
  id?: string;
  isLoading?: boolean;
  timeZone?: string;
  toDate?: Date;
  type?: 'popover' | 'plain';
  yearsRange?: number;
};

export function CalendarDatePickerV2({
  button,
  date,
  defaultEndTime = '23:59',
  defaultStartTime = '00:00',
  disabled = false,
  hasTime = false,
  id,
  isLoading = false,
  mode,
  timeZone = SYSTEM_TIMEZONE,
  type = 'popover',
  yearsRange = 10,
  onChange,
  ...props
}: CalendarDatePickerV2Props) {
  const [hasTouchedStart, setHasTouchedStart] = useState(false);
  const [hasTouchedEnd, setHasTouchedEnd] = useState(false);
  const [startTime, setStartTime] = useState(defaultStartTime);
  const [endTime, setEndTime] = useState(defaultEndTime);
  const isRange = mode === 'range';

  useEffect(() => {
    if (!hasTime || !date) return;

    if (isRange && 'from' in date) {
      if (date?.from && !hasTouchedStart) {
        const time = normalizeTimeFromDate({ date: date.from, timeZone });
        const normalizedDate = buildDateWithTime({
          date: date.from,
          time,
          timeZone,
        });
        setStartTime(format(normalizedDate, FORMAT_TIME));
      }
      if (date?.to && !hasTouchedEnd) {
        const time = normalizeTimeFromDate({ date: date.to, timeZone });
        const normalizedDate = buildDateWithTime({
          date: date.to,
          time,
          timeZone,
        });
        setEndTime(format(normalizedDate, FORMAT_TIME));
      }
    } else if (!isRange && date instanceof Date && !hasTouchedStart) {
      const time = normalizeTimeFromDate({ date, timeZone });
      const normalizedDate = buildDateWithTime({
        date,
        time,
        timeZone,
      });
      setStartTime(format(normalizedDate, FORMAT_TIME));
    }
  }, [date, hasTime, isRange, timeZone, hasTouchedStart, hasTouchedEnd]);

  type HandleTimeChangeParams = {
    type: 'start' | 'end';
    time: string;
  };

  const handleTimeChange = ({ type, time }: HandleTimeChangeParams) => {
    if (!isValidTime(time)) return;

    if (type === 'start') {
      setStartTime(time);
    } else {
      setEndTime(time);
    }

    if (isRange) {
      const from = buildDateWithTime({
        date: date?.from,
        time: type === 'start' ? time : startTime,
        timeZone,
      });
      const to = buildDateWithTime({
        date: date?.to ?? date?.from,
        time: type === 'end' ? time : endTime,
        timeZone,
      });
      onChange({ from, to });
    } else {
      const newDate = buildDateWithTime({
        date,
        time,
        timeZone,
      });
      onChange(newDate);
    }
  };

  const formattedDate = useMemo(
    () => getFormattedDate({ date, hasTime, timeZone }),
    [date, hasTime, timeZone],
  );

  const calendarContent = (
    <div className="flex h-[22.25rem] w-auto p-0">
      {isRange ? (
        <Calendar
          id={id ? `${id}-range-calendar` : undefined}
          defaultMonth={date?.from}
          formatters={{
            formatCaption: (month) => capitalize(format(month, 'MMMM yyyy', { locale: es })),
          }}
          locale={es}
          mode="range"
          numberOfMonths={1}
          onSelect={(range) => {
            if (!range) {
              onChange(undefined);
              return;
            }
            const from = range?.from
              ? buildDateWithTime({
                  date: range.from,
                  time: startTime,
                  timeZone,
                })
              : undefined;
            const to = range?.to
              ? buildDateWithTime({
                  date: range.to,
                  time: endTime,
                  timeZone,
                })
              : undefined;
            onChange({ from, to });
          }}
          selected={date}
          yearsRange={yearsRange}
          {...props}
        />
      ) : (
        <Calendar
          id={id ? `${id}-single-calendar` : undefined}
          defaultMonth={date}
          formatters={{
            formatCaption: (month) => capitalize(format(month, 'MMMM yyyy', { locale: es })),
          }}
          locale={es}
          mode="single"
          numberOfMonths={1}
          onSelect={(day) => {
            onChange(
              day
                ? buildDateWithTime({
                    date: day,
                    time: startTime,
                    timeZone,
                  })
                : undefined,
            );
          }}
          selected={date}
          yearsRange={yearsRange}
          {...props}
        />
      )}
      {hasTime && (
        <>
          <Separator orientation="vertical" />
          <div className="flex w-36 flex-col gap-4 p-4">
            <div className="w-full">
              <LabelWithoutRef>{isRange ? 'Hora de inicio' : 'Hora'}</LabelWithoutRef>
              <Input
                step="60"
                type="time"
                value={startTime || '00:00'}
                onChange={(e) => {
                  const time = e.target.value;
                  if (!isValidTime(time)) return;
                  setHasTouchedStart(true);
                  handleTimeChange({ type: 'start', time });
                }}
                className="appearance-none [&::-webkit-calendar-picker-indicator]:hidden"
              />
            </div>
            {isRange ? (
              <div className="w-full">
                <LabelWithoutRef>Hora de fin</LabelWithoutRef>
                <Input
                  step="60"
                  type="time"
                  value={endTime || '00:00'}
                  onChange={(e) => {
                    const time = e.target.value;
                    if (!isValidTime(time)) return;
                    setHasTouchedEnd(true);
                    handleTimeChange({ type: 'end', time });
                  }}
                  className="appearance-none [&::-webkit-calendar-picker-indicator]:hidden"
                />
              </div>
            ) : null}
          </div>
        </>
      )}
    </div>
  );

  if (type === 'plain') {
    return calendarContent;
  }

  return (
    <div className="relative flex w-full items-center">
      <Popover>
        <PopoverTrigger asChild>
          <Button
            id={id ? `${id}-trigger-calendar` : undefined}
            className={cn('flex h-9 w-full rounded-lg bg-transparent px-3 font-medium shadow-xs', {
              'rounded-r-none border-r-0': button?.onClick,
            })}
            disabled={disabled || isLoading}
            variant="outline"
          >
            <div className="flex w-full items-center justify-between">
              <div className="flex items-center gap-2">
                <CalendarIcon />
                <span>{formattedDate}</span>
              </div>
              <ChevronDownIcon className="size-4 opacity-50" />
            </div>
          </Button>
        </PopoverTrigger>
        <PopoverContent align="start" className="flex h-[22.25rem] w-auto p-0">
          {calendarContent}
        </PopoverContent>
      </Popover>
      {button?.onClick && (
        <Button
          className="rounded-l-none border-l-0 border-input px-4 text-sm"
          disabled={disabled || isLoading}
          id={button?.id ? `${button.id}-trigger-calendar-action` : undefined}
          type="button"
          variant="secondary"
          onClick={button.onClick}
        >
          {button.icon}
          {button.text}
        </Button>
      )}
      {isLoading && <LoadingAddon type="plain" />}
    </div>
  );
}
